import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, KpiCard, Ring, ProgressBar, cn } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type Sla = { work_orders: number; completed: number; first_time_fix: number; within_resolution_sla: number; sla_eligible: number; avg_resolution_minutes: number | null };
type Comp = { total: number; overdue: number; due_j7: number; due_j30: number; compliance_pct: number | null; life_safety_pct: number | null };
type Mix = { wo_type: string; n: number };
type PropRow = { property_id: string; property_name: string; total: number; overdue: number; compliance_pct: number | null };

const pct = (num: number, den: number) => (den > 0 ? Math.round((100 * num) / den) : null);

export function Overview() {
  const { t } = useTranslation();
  const [sla, setSla] = useState<Sla | null>(null);
  const [comp, setComp] = useState<Comp | null>(null);
  const [mix, setMix] = useState<Mix[]>([]);
  const [byProp, setByProp] = useState<PropRow[]>([]);

  useEffect(() => {
    (async () => {
      const [s, c, m, p] = await Promise.all([
        supabase.from('dash_sla').select('*').limit(1).maybeSingle(),
        supabase.from('dash_compliance').select('*').limit(1).maybeSingle(),
        supabase.from('dash_wo_mix').select('*'),
        supabase.from('dash_compliance_property').select('*').order('compliance_pct', { ascending: true }),
      ]);
      setSla((s.data as Sla) ?? null);
      setComp((c.data as Comp) ?? null);
      setMix((m.data as Mix[]) ?? []);
      setByProp((p.data as PropRow[]) ?? []);
    })();
  }, []);

  const slaMet = sla ? pct(sla.within_resolution_sla, sla.sla_eligible) : null;
  const ftf = sla ? pct(sla.first_time_fix, sla.completed) : null;
  const openWos = sla ? sla.work_orders - sla.completed : 0;
  const mttrH = sla?.avg_resolution_minutes != null ? (sla.avg_resolution_minutes / 60).toFixed(1) : null;
  const prev = mix.find((x) => x.wo_type === 'preventive')?.n ?? 0;
  const corr = mix.find((x) => x.wo_type === 'corrective')?.n ?? 0;
  const prevShare = useMemo(() => pct(prev, prev + corr), [prev, corr]);

  return (
    <Layout header={<Topbar />}>
      <div className="flex flex-col gap-[18px] px-7 py-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.overview')}</h1>

        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label={t('kpi.preventiveCompliance')} value={comp?.compliance_pct != null ? `${comp.compliance_pct}%` : '—'} sub={comp?.compliance_pct == null ? t('common.noData') : `${t('kpi.target')} 95%`} dark>
            <Ring value={comp?.compliance_pct ?? 0} onDark label="" />
          </KpiCard>
          <KpiCard label={t('dash.slaMet')} value={slaMet == null ? '—' : `${slaMet}%`} sub={slaMet == null ? t('common.noData') : undefined} />
          <KpiCard label={t('dash.mttr')} value={mttrH == null ? '—' : `${mttrH} h`} sub={mttrH == null ? t('common.noData') : undefined} />
          <KpiCard label={t('kpi.openWorkOrders')} value={openWos} />
        </section>

        <div className="flex flex-wrap items-start gap-[18px]">
          <Card className="min-w-0 flex-[1_1_320px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('dash.woMix')}</h2>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center justify-between"><span>{t('dash.preventive')}</span><span className="font-mono">{prev}</span></div>
              <ProgressBar value={prevShare ?? 0} />
              <div className="flex items-center justify-between"><span>{t('dash.corrective')}</span><span className="font-mono">{corr}</span></div>
              <div className="mt-1 text-xs text-muted">{t('dash.firstTimeFix')}: {ftf == null ? '—' : `${ftf}%`}</div>
            </div>
          </Card>

          <Card className="min-w-0 flex-[2_1_440px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('dash.byProperty')}</h2>
            <div className="flex flex-col gap-3">
              {byProp.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
              {byProp.map((r) => (
                <div key={r.property_id} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between text-sm">
                    <span>{r.property_name}</span>
                    <span className={cn('font-mono', (r.compliance_pct ?? 100) < 80 && 'text-st-overdue-fg')}>{r.compliance_pct ?? '—'}%</span>
                  </div>
                  <ProgressBar value={r.compliance_pct ?? 0} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
